// @ts-nocheck

import React, { useEffect, useState } from "react";
import SoftBox from "../../components/common/softBox";
import DashboardNavbar from "../../components/navbars/dashboardNavbar";
import SoftTypography from "../../components/common/softTypography";
import SoftInput from "../../components/common/softInput";
import SoftButton from "../../components/common/softButton";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { getSapConfig, updateSapConfig } from "../../features/sapConfig";
import { testSapConnectionQuery } from "../../api/sapConfigApi";
import { resolveSapEndpoint, toRelativeSapPath } from "../../utils/sapUrl";
import { toast } from "react-toastify";
import { Grid, Typography } from "@mui/material";

const EMPTY_FORM = {
  inquiry_order_type: "",
  sale_order_type: "",
  return_order_doc_type: "",
  expiry_order_doc_type: "",
  property_id: "",
  widget_id: "",
  sap_base_url: "",
  sap_username: "",
  sap_password: "",
  sap_client: "",
  get_company_details_url: "",
  get_credit_limit_url: "",
  get_material_list_url: "",
  create_sales_order_url: "",
  delete_sale_order_url: "",
  get_invoice_pdf_url: "",
  get_sales_orders_url: "",
  get_sales_order_detail_url: "",
  get_sales_order_pdf_url: "",
  get_invoices_statement_url: "",
  create_sale_order_requisition_url: "",
  get_sale__order_requisitions_url: "",
  get_sale_order_requisition_details_url: "",
  update_sale_order_requisition_url: "",
  get_expired_materials_list_url: "",
  get_return_order_reasons_list_url: "",
  create_sale_order_return_url: "",
  create_return_order_without_reference_url: "",
  get_sales_order_org_data_url: "",
  get_sales_plant_storage_location_url: "",
  get_invoice_statement_pdf_download_url: "",
  selected_companies: [],
};

const DOCUMENT_TYPE_FIELDS = [
  { name: "inquiry_order_type", label: "Inquiry Order Type" },
  { name: "sale_order_type", label: "Sale Order Type" },
  { name: "return_order_doc_type", label: "Return Order Document Type" },
  { name: "expiry_order_doc_type", label: "Expiry Order Document Type" },
];

const APPLICATION_FIELDS = [
  { name: "property_id", label: "Property ID" },
  { name: "widget_id", label: "Widget ID" },
];

const CONNECTION_FIELDS = [
  { name: "sap_base_url", label: "SAP Base URL" },
  { name: "sap_username", label: "SAP Username" },
  { name: "sap_password", label: "SAP Password", type: "password" },
  { name: "sap_client", label: "SAP Client" },
];

const ENDPOINT_FIELDS = [
  { name: "get_company_details_url", label: "Get Company Details Path" },
  { name: "get_credit_limit_url", label: "Get Credit Limit Path" },
  { name: "get_material_list_url", label: "Get Material List Path" },
  { name: "create_sales_order_url", label: "Create Sales Order Path" },
  { name: "delete_sale_order_url", label: "Delete Sales Order Path" },
  { name: "get_invoice_pdf_url", label: "Get Invoice PDF Path" },
  { name: "get_sales_orders_url", label: "Get Sales Orders Path" },
  { name: "get_sales_order_detail_url", label: "Get Sales Order Detail Path" },
  { name: "get_sales_order_pdf_url", label: "Get Sales Order PDF Path" },
  { name: "get_invoices_statement_url", label: "Get Invoices Statement Path" },
  { name: "create_sale_order_requisition_url", label: "Create Sale Order Requisition Path" },
  { name: "get_sale__order_requisitions_url", label: "Get Sale Order Requisitions Path" },
  { name: "get_sale_order_requisition_details_url", label: "Get Sale Order Requisition Details Path" },
  { name: "update_sale_order_requisition_url", label: "Update Sale Order Requisition Path" },
  { name: "get_expired_materials_list_url", label: "Get Expired Materials List Path" },
  { name: "get_return_order_reasons_list_url", label: "Get Return Order Reasons List Path" },
  { name: "create_sale_order_return_url", label: "Create Sale Order Return Path" },
  { name: "create_return_order_without_reference_url", label: "Create Return Order Without Reference Path" },
  { name: "get_sales_order_org_data_url", label: "Get Sales Order Org Data Path" },
  { name: "get_sales_plant_storage_location_url", label: "Get Sales Plant Storage Location Path" },
  { name: "get_invoice_statement_pdf_download_url", label: "Get Invoice Statement PDF Download Path" },
];

const ENDPOINT_NAMES = new Set(ENDPOINT_FIELDS.map((field) => field.name));

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SoftBox mt={3} mb={1}>
    <SoftTypography variant="h6" fontWeight="bold" textAlign="left">
      {children}
    </SoftTypography>
  </SoftBox>
);

const SapConfig: React.FC = () => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [testingConnection, setTestingConnection] = useState(false);
  // Held in state (not only toasted) so a failure stays visible on the page.
  const [connectionResult, setConnectionResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state: any) => state.sapConfig);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ENDPOINT_NAMES.has(name)
        ? toRelativeSapPath(prev.sap_base_url, value)
        : value,
    }));
  };

  useEffect(() => {
    dispatch(getSapConfig());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setFormData({ ...EMPTY_FORM, ...data });
    }
  }, [data]);

  const handleSubmit = () => {
    // Store endpoints as paths relative to the SAP base URL.
    const normalized = { ...formData };
    ENDPOINT_FIELDS.forEach(({ name }) => {
      normalized[name] = toRelativeSapPath(
        formData.sap_base_url,
        formData[name]
      );
    });

    dispatch(updateSapConfig({ data: normalized })).then((resultAction) => {
      if (updateSapConfig.rejected.match(resultAction)) {
        console.error(resultAction.payload || resultAction.error);
      } else {
        toast.success("SAP Config updated successfully");
      }
    });
  };

  const handleTestConnection = () => {
    if (!formData.sap_base_url) {
      setConnectionResult({
        ok: false,
        message: "Enter the SAP Base URL before testing the connection.",
      });
      return;
    }

    setTestingConnection(true);
    setConnectionResult(null);

    testSapConnectionQuery({
      sap_base_url: formData.sap_base_url,
      sap_username: formData.sap_username,
      sap_password: formData.sap_password,
      sap_client: formData.sap_client,
    })
      .then((res) => {
        const result = {
          ok: res?.status !== false,
          message:
            res?.message ||
            (res?.status === false
              ? "SAP connection failed."
              : "SAP connection successful."),
        };
        setConnectionResult(result);
        if (result.ok) toast.success(result.message);
        else toast.error(result.message);
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.message ||
          "Failed to test the SAP connection.";
        setConnectionResult({ ok: false, message });
        toast.error(message);
      })
      .finally(() => setTestingConnection(false));
  };

  const renderInput = (field: {
    name: string;
    label: string;
    type?: string;
  }) => {
    const isEndpoint = ENDPOINT_NAMES.has(field.name);
    return (
      <Grid item md={4} xs={12} key={field.name}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography
              component="label"
              variant="caption"
              fontWeight="bold"
            >
              {field.label}
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type={field.type || "text"}
            name={field.name}
            placeholder={
              isEndpoint ? "e.g. /ZAPI_CUSTOMER_DATA_SRV/GET_CUSTOMERSet" : `Enter ${field.label}`
            }
            value={formData[field.name]}
            onChange={handleChange}
          />
          {isEndpoint && formData[field.name] ? (
            <SoftBox mt={0.5} ml={0.5}>
              <SoftTypography
                variant="caption"
                sx={{ color: "#7b809a", wordBreak: "break-all" }}
              >
                {resolveSapEndpoint(formData.sap_base_url, formData[field.name])}
              </SoftTypography>
            </SoftBox>
          ) : null}
        </SoftBox>
      </Grid>
    );
  };

  return (
    <SoftBox className="language-box">
      <DashboardNavbar />
      <SoftBox my={2}>
        <SoftTypography variant="h4" fontWeight="bold" textAlign="left">
          SAP Configuration
        </SoftTypography>
      </SoftBox>

      <SoftBox component="form" role="form">
        <SectionTitle>Document Types</SectionTitle>
        <Grid container spacing={2}>
          {DOCUMENT_TYPE_FIELDS.map(renderInput)}
        </Grid>

        <SectionTitle>Application</SectionTitle>
        <Grid container spacing={2}>
          {APPLICATION_FIELDS.map(renderInput)}
        </Grid>

        <SectionTitle>SAP Connection</SectionTitle>
        <Grid container spacing={2}>
          {CONNECTION_FIELDS.map(renderInput)}
        </Grid>

        <SoftBox mt={1} mb={2} display="flex" alignItems="center" gap={2}>
          <SoftButton
            variant="outlined"
            color="dark"
            type="button"
            onClick={handleTestConnection}
            disabled={testingConnection}
          >
            {testingConnection ? "Testing..." : "Test Connection"}
          </SoftButton>
        </SoftBox>

        {connectionResult && (
          <SoftBox mb={2}>
            <Typography
              variant="body2"
              sx={{
                color: connectionResult.ok ? "#2e7d32" : "#c62828",
                fontWeight: 600,
              }}
            >
              {connectionResult.message}
            </Typography>
          </SoftBox>
        )}

        <SectionTitle>
          Service Endpoints (paths relative to SAP Base URL)
        </SectionTitle>
        <Grid container spacing={2}>
          {ENDPOINT_FIELDS.map(renderInput)}
        </Grid>

        <SoftBox mt={4} mb={1}>
          <SoftButton
            variant="gradient"
            color="final"
            fullWidth
            onClick={handleSubmit}
          >
            Save
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

export default SapConfig;
